import { useEffect, useState } from 'react';
import { Socket } from 'socket.io-client';
import { TEAM_FACTION_NAMES, getDisplayTeamBg } from '../displayTheme';

type TeamColor = 'BLUE' | 'RED' | 'GREEN' | 'YELLOW';

interface PollOption {
  id: string;
  text: string;
}

interface PollVotingDisplayProps {
  pollId: string;
  targetTeam?: TeamColor;
  socket: Socket | null;
}

function PollVotingDisplay({ pollId, targetTeam, socket }: PollVotingDisplayProps) {
  const [options, setOptions] = useState<PollOption[]>([]);
  const [remainingSeconds, setRemainingSeconds] = useState<number>(0);

  // Fetch poll data
  useEffect(() => {
    const fetchPoll = async () => {
      try {
        const response = await fetch(`/api/polls/${pollId}`);
        if (response.ok) {
          const data = await response.json();
          setOptions(data.poll.options || []);
          // Calcola tempo rimanente
          if (data.poll.startedAt) {
            const startTime = new Date(data.poll.startedAt).getTime();
            const elapsed = Math.floor((Date.now() - startTime) / 1000);
            const remaining = Math.max(0, data.poll.durationSeconds - elapsed);
            setRemainingSeconds(remaining);
          }
        }
      } catch (error) {
        console.error('[PollVotingDisplay] Error fetching poll:', error);
      }
    };

    fetchPoll();
  }, [pollId]);

  // Ascolta tick dal socket esistente
  useEffect(() => {
    if (!socket) return;

    const handleTick = (data: { pollId: string; remainingSeconds: number }) => {
      if (data.pollId === pollId) {
        setRemainingSeconds(data.remainingSeconds);
      }
    };

    socket.on('poll:tick', handleTick);

    return () => {
      socket.off('poll:tick', handleTick);
    };
  }, [socket, pollId]);

  const bgClass = getDisplayTeamBg(targetTeam);
  const count = options.length;
  const isCompact = count >= 5;

  return (
    <div className={`h-screen w-screen flex flex-col items-center p-8 ${bgClass}`}>
      {/* Timer */}
      <div className={`shrink-0 text-center ${isCompact ? 'mb-4' : 'mb-8'}`}>
        <div className={`font-bold font-heading ${isCompact ? 'text-7xl' : 'text-9xl'} ${remainingSeconds <= 5 ? 'text-red-400 animate-pulse' : 'text-parchment-100'}`}>
          {remainingSeconds}
        </div>
        <p className={`text-parchment-400 font-heading ${isCompact ? 'text-xl' : 'text-3xl mt-2'}`}>secondi</p>
      </div>

      {/* Titolo squadra */}
      {targetTeam && (
        <p className={`text-parchment-200 font-heading shrink-0 ${isCompact ? 'text-2xl mb-4' : 'text-4xl mb-8'}`}>
          Squadra {TEAM_FACTION_NAMES[targetTeam]} sta votando
        </p>
      )}

      {/* Opzioni */}
      <div className={`w-full max-w-4xl flex-1 flex flex-col justify-center ${isCompact ? 'gap-3' : 'gap-6'}`}>
        {options.map((option, index) => (
          <div
            key={option.id}
            className={`display-panel ${isCompact ? 'p-4' : 'p-8'}`}
          >
            <div className={`flex items-center ${isCompact ? 'gap-4' : 'gap-6'}`}>
              <span className={`font-bold text-parchment-400 font-heading ${isCompact ? 'text-3xl' : 'text-5xl'}`}>{index + 1}</span>
              <p className={`font-semibold text-parchment-100 font-heading ${isCompact ? 'text-2xl' : 'text-4xl'}`}>{option.text}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default PollVotingDisplay;
